import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Navigation, FileDown, Loader2, FileSearch, MapPinned, Banknote } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DocImage } from "@/components/DocImage";
import { api, errMsg } from "@/lib/api";
import { fmtCoord, mapsUrl } from "@/lib/geoPhoto";
import { downloadLoanLetter } from "@/lib/loanPdf";

const GeoInfo = ({ g, id }) => (
  <div className="text-[10.5px] text-slate-600 mt-1 leading-tight" data-testid={`admin-loan-geo-${id}`}>
    <div className="flex items-center gap-1"><MapPin className="h-3 w-3 text-red-600 shrink-0" />{fmtCoord(g)}{g.accuracy ? ` ±${g.accuracy}m` : ""}</div>
    <div>{new Date(g.taken_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</div>
    <a href={mapsUrl(g)} target="_blank" rel="noreferrer" data-testid={`admin-loan-maps-${id}`}
      className="inline-flex items-center gap-1 mt-1 font-semibold text-blue-700 border border-blue-200 bg-blue-50 rounded-md px-1.5 py-0.5"><Navigation className="h-3 w-3" />Buka di Google Maps</a>
  </div>
);

export const LoanPhotos = ({ loan }) => {
  const [big, setBig] = useState(null);
  const photos = (loan.documents || []).filter((d) => ["selfie", "rumah"].includes(d.doc_type));
  if (!photos.length) return null;
  return (
    <div className="flex flex-wrap gap-3 mt-2" data-testid={`admin-loan-photos-${loan.loan_id}`}>
      {photos.map((d) => (
        <div key={d.doc_id} className="w-48">
          <div className="text-[11px] text-slate-500 mb-0.5">{d.label}</div>
          <button type="button" onClick={() => setBig(d)} data-testid={`admin-loan-photo-${d.doc_type}-${loan.loan_id}`} className="block w-48 h-32 rounded-lg overflow-hidden border border-slate-200 hover:ring-2 hover:ring-blue-400">
            <DocImage docId={d.doc_id} alt={d.label} className="w-full h-full object-cover" />
          </button>
          {d.geo && <GeoInfo g={d.geo} id={loan.loan_id} />}
        </div>
      ))}
      <Dialog open={!!big} onOpenChange={(o) => !o && setBig(null)}>
        <DialogContent className="max-w-2xl" data-testid="admin-loan-photo-dialog">
          <DialogHeader><DialogTitle>{big?.label} — {loan.member_name}</DialogTitle></DialogHeader>
          {big && <DocImage docId={big.doc_id} alt={big.label} className="w-full max-h-[70vh] object-contain rounded-lg bg-slate-50" />}
          {big?.geo && <GeoInfo g={big.geo} id={`dialog-${loan.loan_id}`} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const LoanPdfButton = ({ loan }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await downloadLoanLetter(loan); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy} data-testid={`admin-loan-pdf-${loan.loan_id}`}
      className="h-9 px-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-xs font-bold text-slate-800 inline-flex items-center gap-1.5 disabled:opacity-60">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}Surat Pengajuan PDF
    </button>
  );
};

const NEXT = { diajukan: ["verifikasi", "Mulai Verifikasi Dokumen", FileSearch], verifikasi: ["survei", "Lanjut ke Survei Lapangan", MapPinned] };

export const StageButton = ({ loan, onDone }) => {
  const [busy, setBusy] = useState(false);
  const isDisburse = loan.status === "disetujui";
  const next = NEXT[loan.stage || "diajukan"];
  if (!isDisburse && !(loan.status === "pending" && next)) return null;
  const [stage, label, Icon] = isDisburse ? [null, "Cairkan Dana", Banknote] : next;
  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/loans/${loan.loan_id}/${isDisburse ? "disburse" : "stage"}`, isDisburse ? { approve: true } : { stage });
      toast.success(isDisburse ? "Dana pinjaman dicairkan — lihat di tab Semua" : `Tahap: ${label.replace(/^(Mulai|Lanjut ke) /, "")}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy} data-testid={`admin-loan-${isDisburse ? "disburse" : "stage"}-${loan.loan_id}`}
      className="h-9 px-3 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}{label}
    </button>
  );
};
